import { gql } from "@apollo/client";

export const COMPETITION_COMMENT_FIELDS = gql`
  fragment CompetitionCommentFields on Comment {
    id
    _id
    createdAt
    deletedAt
    text
    gifUrl
    user {
      _id
      avatarUrl: avatar
      username
      userGroup {
        type
      }
    }
  }
`;

export const GET_COMPETITION_COMMENT_COUNT = gql`
  query CompetitionCommentCount($id: String!) {
    competition(id: $id) {
      id
      comments(first: 0, excludeDeleted: true) {
        pageInfo {
          totalCount
        }
      }
    }
  }
`;

export const GET_COMPETITION_COMMENTS = gql`
  ${COMPETITION_COMMENT_FIELDS}

  query CompetitionComments($id: String!, $first: Int, $after: String) {
    competition(id: $id) {
      id
      comments(first: $first, after: $after, excludeDeleted: true) {
        pageInfo {
          endCursor
          hasNextPage
          totalCount
        }
        nodes {
          ...CompetitionCommentFields
        }
      }
    }
  }
`;

export const COMPETITION_COMMENT_CREATE = gql`
  ${COMPETITION_COMMENT_FIELDS}

  mutation CompetitionCommentCreate(
    $commentableId: String!
    $text: String!
    $gifUrl: String
  ) {
    commentCreate(
      input: { commentableId: $commentableId, text: $text, gifUrl: $gifUrl }
    ) {
      errors {
        message
      }
      success
      comment {
        ...CompetitionCommentFields
      }
    }
  }
`;

export const COMPETITION_COMMENT_DELETE = gql`
  ${COMPETITION_COMMENT_FIELDS}

  mutation CompetitionCommentDelete($commentId: String!) {
    commentDelete(input: { commentId: $commentId }) {
      errors {
        message
      }
      success
      comment {
        ...CompetitionCommentFields
      }
    }
  }
`;

export const COMPETITION_FIELDS = gql`
  fragment CompetitionFields on Competition {
    id
    _id
    title
    description
    active
    visibility
    sponsored
    approvalStatus
    startsAt
    endsAt
    bannerGraphic
    creatorUser {
      id
      username
      avatarUrl
      userGroup {
        type
      }
    }
    participantCount
    totalSubmissions
    entryRequirement
    submissionRule
    clipRequirement
    rankingConfig
    prizes
    myEntry {
      id
      competitionId
      userId
      score
      qualified
      disqualified
      disqualificationReason
      clipsUntilQualified
      dailySubmissions {
        limit
        used
        remaining
      }
      submittedClips {
        id
        shareId
        title
        score
        views
        createdAt
        thumbnailUrl(style: STANDARD)
      }
    }
  }
`;

export const GET_CREATOR_COMPETITIONS = gql`
  query GetCreatorCompetitions(
    $status: CompetitionStatus
    $sort: CompetitionSort
    $visibility: String
    $first: Int
    $after: String
  ) {
    creatorCompetitions(
      status: $status
      sort: $sort
      visibility: $visibility
      first: $first
      after: $after
    ) {
      nodes {
        ...CompetitionFields
      }
      pageInfo {
        hasNextPage
        hasPreviousPage
        endCursor
        startCursor
        totalCount
      }
    }
  }
  ${COMPETITION_FIELDS}
`;

// One request for the homepage banner: each status is fetched via an alias
// so no pool can crowd another out of its "first" window (an un-filtered
// ENDING_SOONEST query returns already-ended competitions first).
export const GET_HOMEPAGE_COMPETITIONS = gql`
  query GetHomepageCompetitions($first: Int) {
    active: creatorCompetitions(
      status: ACTIVE
      sort: ENDING_SOONEST
      first: $first
    ) {
      nodes {
        ...CompetitionFields
      }
    }
    upcoming: creatorCompetitions(
      status: UPCOMING
      sort: ENDING_SOONEST
      first: $first
    ) {
      nodes {
        ...CompetitionFields
      }
    }
    ended: creatorCompetitions(
      status: ENDED
      sort: ENDING_SOONEST
      first: $first
    ) {
      nodes {
        ...CompetitionFields
      }
    }
  }
  ${COMPETITION_FIELDS}
`;

export const GET_MY_COMPETITIONS = gql`
  query GetMyCompetitions($sort: CompetitionSort, $first: Int, $after: String) {
    myCompetitions(sort: $sort, first: $first, after: $after) {
      nodes {
        ...CompetitionFields
      }
      pageInfo {
        hasNextPage
        hasPreviousPage
        endCursor
        startCursor
        totalCount
      }
    }
  }
  ${COMPETITION_FIELDS}
`;

export const GET_COMPETITION = gql`
  query GetCompetition($id: String!) {
    competition(id: $id) {
      ...CompetitionFields
      creator
      linkedToDiscord
      discordLinkCode
      twitch {
        login
        linkCode
      }
      eligibility {
        eligible
        errors {
          code
          field
          message
        }
      }
      challenges {
        id
        challengeType
        title
        description
        image
        required
        completed
        visitUrl
        scoreModifier
        discordInviteUrl
      }
      leaderboard(limit: 10) {
        topEntries {
          rank
          score
          submittedClipCount
          qualified
          userId
          user {
            id
            username
            avatarUrl
            userGroup {
              type
            }
          }
        }
        myEntry {
          rank
          score
          submittedClipCount
          qualified
          userId
          user {
            id
            username
            avatarUrl
            userGroup {
              type
            }
          }
        }
      }
    }
  }
  ${COMPETITION_FIELDS}
`;

export const GET_COMPETITION_LEADERBOARD = gql`
  query CompetitionLeaderboard($id: String!, $limit: Int) {
    competitionLeaderboard(id: $id, limit: $limit) {
      topEntries {
        rank
        score
        submittedClipCount
        qualified
        userId
        user {
          id
          username
          avatarUrl
          userGroup {
            type
          }
        }
      }
      myEntry {
        rank
        score
        submittedClipCount
        qualified
        userId
        user {
          id
          username
          avatarUrl
          userGroup {
            type
          }
        }
      }
    }
  }
`;

export const GET_ELIGIBLE_CLIPS = gql`
  query GetEligibleClips(
    $competitionId: String!
    $sort: ClipSort
    $first: Int
    $after: String
  ) {
    eligibleClips(
      competitionId: $competitionId
      sort: $sort
      first: $first
      after: $after
    ) {
      nodes {
        id
        shareId
        title
        score
        createdAt
        thumbnailUrl(style: STANDARD)
        tags {
          key
          value
        }
      }
      pageInfo {
        hasNextPage
        endCursor
        totalCount
      }
    }
  }
`;

/* Signature competitions need a brand-new signature applied, which only
   happens on a remix — so a user's existing clips are never eligible and the
   picker comes up empty. These are the clips that clear every *other*
   requirement and would qualify once remixed. Non-signature competitions get
   an empty list from the server, so callers gate on the requirement instead
   of on this being present.

   Args match the server signature exactly (competitionId + sort); there is no
   first/after here, so the section renders whatever one page returns. */
export const GET_REMIXABLE_CLIPS = gql`
  query GetRemixableClips($competitionId: String!, $sort: ClipSort) {
    remixableClips(competitionId: $competitionId, sort: $sort) {
      nodes {
        id
        shareId
        title
        score
        createdAt
        thumbnailUrl(style: STANDARD)
        tags {
          key
          value
        }
        game
        moment {
          id
        }
      }
      pageInfo {
        hasNextPage
        endCursor
        totalCount
      }
    }
  }
`;

/* platform: WEB is mandatory from a browser — the API defaults to APP and
   redirects to allstar://, which a browser can't follow. `state` is our own
   nonce; the API prefixes it with the platform on the way out and hands the
   bare value back on return, so it doubles as CSRF protection. */
export const GET_TWITCH_CHANNEL_AUTH_URL = gql`
  query GetTwitchChannelAuthUrl($platform: AuthPlatform, $state: String) {
    authUrlTwitchChannel(platform: $platform, state: $state)
  }
`;

/* Entrant equivalent — requests no scopes, only identity. Deliberately not
   authUrl(source: TWITCH): AuthSource is an input enum declared in more than
   one subgraph, and federation intersects those, so TWITCH is dropped at the
   gateway. This field sidesteps the enum entirely. */
export const GET_TWITCH_AUTH_URL = gql`
  query GetTwitchAuthUrl($platform: AuthPlatform, $state: String) {
    authUrlTwitch(platform: $platform, state: $state)
  }
`;

export const USER_LINK_TWITCH = gql`
  mutation UserLinkTwitch($input: UserLinkTwitchInput!) {
    userLinkTwitch(input: $input) {
      viewer {
        twitchLogin
      }
      errors {
        code
        field
        message
      }
    }
  }
`;

/* The sponsor's exchange. Deliberately unauthenticated and keyed on the
   link code rather than a competitionId — whoever owns the channel has no
   Allstar account and never needs one. */
export const COMPETITION_LINK_TWITCH_CHANNEL = gql`
  mutation CompetitionLinkTwitchChannel(
    $input: CompetitionLinkTwitchChannelInput!
  ) {
    competitionLinkTwitchChannel(input: $input) {
      competition {
        id
        twitch {
          login
        }
      }
      errors {
        code
        field
        message
      }
    }
  }
`;

/* Creator-only. Each call replaces the previous code, so regenerating
   invalidates any link already sent out. */
export const COMPETITION_GENERATE_TWITCH_LINK_CODE = gql`
  mutation CompetitionGenerateTwitchLinkCode(
    $input: CompetitionGenerateTwitchLinkCodeInput!
  ) {
    competitionGenerateTwitchLinkCode(input: $input) {
      competition {
        id
        twitch {
          login
          linkCode
        }
      }
      errors {
        code
        field
        message
      }
    }
  }
`;

export const GET_MY_TWITCH_LOGIN = gql`
  query GetMyTwitchLogin {
    me {
      twitchLogin
    }
  }
`;

export const GET_ELIGIBILITY_COMPETITIONS = gql`
  query GetEligibilityCompetitions($sort: CompetitionSort, $first: Int) {
    creatorCompetitions(sort: $sort, first: $first) {
      nodes {
        id
        _id
        title
        startsAt
        endsAt
        visibility
        myEntry {
          submittedClips {
            shareId
          }
        }
      }
    }
  }
`;

export const GET_MY_COMPETITION_ENTRY = gql`
  query GetMyCompetitionEntry($competitionId: String!) {
    myCompetitionEntry(competitionId: $competitionId) {
      id
      competitionId
      userId
      score
      qualified
      disqualified
      disqualificationReason
      clipsUntilQualified
      dailySubmissions {
        limit
        used
        remaining
      }
      submittedClips {
        id
        shareId
        title
        score
        views
        createdAt
        thumbnailUrl(style: STANDARD)
      }
    }
  }
`;

export const GET_COMPETITION_FEED = gql`
  query GetCompetitionFeed(
    $id: String!
    $first: Int
    $after: String
    $sort: CompetitionFeedSort
    $submitterId: String
  ) {
    competitionFeed(
      id: $id
      first: $first
      after: $after
      sort: $sort
      submitterId: $submitterId
    ) {
      nodes {
        id
        submittedAt
        clip {
          id
          shareId
          title
          score
          views
          createdAt
          game
          matchId
          playerGameIdentifier
          status
          thumbnailUrl(style: STANDARD)
          videoUrl: url(orientation: LANDSCAPE)
          expires {
            expiresAt
            reason
          }
          tags {
            key
            value
          }
          user {
            id
            username
            avatarUrl
            userGroup {
              type
            }
          }
        }
      }
      pageInfo {
        hasNextPage
        endCursor
      }
    }
  }
`;

export const COMPETITION_CREATE = gql`
  mutation CompetitionCreate($input: CompetitionCreateInput!) {
    competitionCreate(input: $input) {
      competition {
        ...CompetitionFields
      }
      errors {
        field
        code
        message
      }
    }
  }
  ${COMPETITION_FIELDS}
`;

export const COMPETITION_JOIN = gql`
  mutation CompetitionJoin($input: CompetitionJoinInput!) {
    competitionJoin(input: $input) {
      competitionEntry {
        id
        competitionId
        userId
        score
        submittedClips {
          id
          shareId
          title
          score
          views
          createdAt
          thumbnailUrl(style: STANDARD)
        }
      }
      errors {
        field
        code
        message
      }
    }
  }
`;

export const COMPETITION_GENERATE_DISCORD_LINK_CODE = gql`
  mutation CompetitionGenerateDiscordLinkCode(
    $input: CompetitionGenerateDiscordLinkCodeInput!
  ) {
    competitionGenerateDiscordLinkCode(input: $input) {
      competition {
        id
        discordLinkCode
        linkedToDiscord
      }
      errors {
        field
        code
        message
      }
    }
  }
`;

export const COMPETITION_CHALLENGE_COMPLETE = gql`
  mutation CompetitionChallengeComplete(
    $input: CompetitionChallengeCompleteInput!
  ) {
    competitionChallengeComplete(input: $input) {
      challenge {
        id
        completed
      }
      errors {
        field
        code
        message
      }
    }
  }
`;

export const COMPETITION_UPDATE = gql`
  mutation CompetitionUpdate($input: CompetitionUpdateInput!) {
    competitionUpdate(input: $input) {
      competition {
        ...CompetitionFields
      }
      errors {
        field
        code
        message
      }
    }
  }
  ${COMPETITION_FIELDS}
`;

export const COMPETITION_SUBMIT_CLIPS = gql`
  mutation CompetitionSubmit($input: CompetitionSubmitInput!) {
    competitionSubmit(input: $input) {
      competitionSubmissions {
        id
        clipId
      }
      errors {
        field
        code
        message
      }
    }
  }
`;

export const COMPETITION_DISQUALIFY = gql`
  mutation CompetitionDisqualify($input: CompetitionDisqualifyInput!) {
    competitionDisqualify(input: $input) {
      competitionEntry {
        id
        userId
        disqualified
        disqualificationReason
      }
      errors {
        field
        code
        message
      }
    }
  }
`;

export const COMPETITION_REMOVE_CLIP = gql`
  mutation CompetitionRemoveClip($input: CompetitionRemoveClipInput!) {
    competitionRemoveClip(input: $input) {
      success
    }
  }
`;

export const COMPETITION_WINNER_FIELDS = gql`
  fragment CompetitionWinnerFields on CompetitionWinner {
    id
    userId
    prizeId
    placement
    awardMethod
    challengeId
    email
    steamId
    user {
      id
      username
      avatarUrl
      userGroup {
        type
      }
    }
  }
`;

export const GET_COMPETITION_MANAGE = gql`
  ${COMPETITION_WINNER_FIELDS}

  query GetCompetitionManage($id: String!, $limit: Int) {
    competition(id: $id) {
      id
      _id
      title
      startsAt
      endsAt
      participantCount
      totalSubmissions
      prizes
      prizeWinners {
        placement
        winner {
          userId
          username
          email
        }
      }
      winners {
        ...CompetitionWinnerFields
      }
      leaderboard(limit: $limit) {
        topEntries {
          rank
          score
          submittedClipCount
          qualified
          userId
          user {
            id
            username
            avatarUrl
            steam {
              steamid
            }
            userGroup {
              type
            }
          }
        }
      }
    }
  }
`;

export const COMPETITION_FINALIZE = gql`
  ${COMPETITION_WINNER_FIELDS}

  mutation CompetitionFinalize($input: CompetitionFinalizeInput!) {
    competitionFinalize(input: $input) {
      winners {
        ...CompetitionWinnerFields
      }
      errors {
        field
        code
        message
      }
    }
  }
`;

export const COMPETITION_RAFFLE_DRAW = gql`
  ${COMPETITION_WINNER_FIELDS}

  mutation CompetitionRaffleDraw($input: CompetitionRaffleDrawInput!) {
    competitionRaffleDraw(input: $input) {
      winner {
        ...CompetitionWinnerFields
      }
      errors {
        field
        code
        message
      }
    }
  }
`;

export const GET_COMPETITION_PRIZE_WINNERS = gql`
  query GetCompetitionPrizeWinners($id: String!) {
    competition(id: $id) {
      id
      prizeWinners {
        placement
        prizeType
        tier
        duration
        description
        winner {
          userId
          username
          email
          currentTier
        }
      }
    }
  }
`;
